import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clientService from '../services/clientService';
import AppShell from './shell/AppShell';
import ClientStageManager from './ClientStageManager';
import PipelineRequestForm from './PipelineRequestForm';
import SubmitSearchModal from './SubmitSearchModal';
import useSidebarOrder from '../hooks/useSidebarOrder';
import { exportClientSearchesToCSV } from '../utils/exportUtils';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');

  // Modals
  const [managingStages, setManagingStages] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [submitModal, setSubmitModal] = useState(null);

  // Sidebar ordering
  const [savedTabOrder, setSavedTabOrder] = useSidebarOrder('client_dashboard');

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [pipelinesData, statsData] = await Promise.all([
        clientService.getMyPipelines(),
        clientService.getDashboardStats(),
      ]);
      setPipelines(pipelinesData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Categorize pipelines
  const draftPipelines = useMemo(() => pipelines.filter(p => p.status === 'draft'), [pipelines]);
  const activePipelines = useMemo(() => pipelines.filter(p => p.status === 'active' && !p.is_completed), [pipelines]);
  const completedPipelines = useMemo(() => pipelines.filter(p => p.status === 'active' && p.is_completed), [pipelines]);

  const categoryData = useMemo(() => {
    switch (activeTab) {
      case 'active': return activePipelines;
      case 'completed': return completedPipelines;
      case 'draft': return draftPipelines;
      default: return activePipelines;
    }
  }, [activeTab, activePipelines, completedPipelines, draftPipelines]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = categoryData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'candidates': {
          const aCount = getTotalCandidates(a);
          const bCount = getTotalCandidates(b);
          return bCount - aCount;
        }
        case 'created_at':
        default: return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });
  }, [categoryData, searchQuery, sortBy]);

  // Sidebar items
  const tabMeta = {
    active: {
      title: 'Active Searches',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      count: activePipelines.length,
    },
    completed: {
      title: 'Completed',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      count: completedPipelines.length,
    },
    draft: {
      title: 'Drafts',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      count: draftPipelines.length,
    },
  };

  const sidebarItems = useMemo(() => {
    const order = savedTabOrder || ['active', 'completed', 'draft'];
    return order.map(tab => {
      const meta = tabMeta[tab];
      if (!meta) return null;
      return {
        key: tab,
        label: null,
        title: meta.title,
        icon: meta.icon,
        badge: meta.count,
        active: activeTab === tab,
        onClick: () => setActiveTab(tab),
      };
    }).filter(Boolean);
  }, [savedTabOrder, activeTab, activePipelines.length, completedPipelines.length, draftPipelines.length]);

  const handleSidebarReorder = useCallback((newItems) => {
    setSavedTabOrder(newItems.map(i => i.key));
  }, [setSavedTabOrder]);

  // Helpers
  const getTotalCandidates = (pipeline) => {
    if (!pipeline.stage_summary || !Array.isArray(pipeline.stage_summary)) return 0;
    return pipeline.stage_summary.reduce((sum, s) => sum + (s.count || 0), 0);
  };

  const getStageChips = (pipeline) => {
    if (!pipeline.stage_summary || !Array.isArray(pipeline.stage_summary)) return [];
    return pipeline.stage_summary
      .filter(s => s.count > 0)
      .map(s => ({
        name: s.name === 'Sent' ? 'In Review' : s.name,
        count: s.count,
      }));
  };

  const getStageColor = (name) => {
    const n = name.toLowerCase();
    if (n.includes('interview')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (n.includes('review') || n.includes('sent')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (n.includes('placed')) return 'bg-green-50 text-green-700 border-green-200';
    if (n.includes('disqualif')) return 'bg-red-50 text-red-600 border-red-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  // Actions
  const handleToggleCompletion = async (pipelineId, e) => {
    e.stopPropagation();
    try {
      await clientService.togglePipelineCompletion(pipelineId);
      await loadDashboard();
    } catch (err) {
      console.error('Error toggling completion:', err);
    }
  };

  const handleDeletePipeline = async (pipelineId, pipelineName, e) => {
    e.stopPropagation();
    if (!confirm(`Delete "${pipelineName}"? This is permanent and cannot be undone.`)) return;
    try {
      await clientService.deletePipeline(pipelineId);
      await loadDashboard();
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSubmitPipeline = (pipelineId) => {
    const p = pipelines.find(x => x.id === pipelineId);
    setSubmitModal({ id: pipelineId, name: p?.name || 'Search' });
  };

  const handleExport = useCallback(() => {
    exportClientSearchesToCSV(filtered, activeTab === 'draft' ? 'drafts' : activeTab);
  }, [filtered, activeTab]);

  // ── PAGE STRIP ────────────────────────────────────────────────────────
  // Client search tabs (Active / Completed / Drafts) moved to the
  // shell's page strip. Each pill shows its icon + label + count.
  // Export button on the right (was the sidebar's onExport handler).
  const pageStrip = (
    <div className="h-10 flex items-center px-4 gap-1.5 overflow-x-auto">
      {sidebarItems.map((item) => (
        <button
          key={item.key}
          onClick={item.onClick}
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            item.active
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={item.title}
        >
          {item.title}
          {item.badge != null && item.badge > 0 && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                item.active ? 'bg-emerald-200 text-emerald-900' : 'bg-white text-gray-600'
              }`}
            >
              {item.badge}
            </span>
          )}
        </button>
      ))}
      <div className="flex-1" />
      <button
        onClick={handleExport}
        className="flex-shrink-0 text-xs px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center gap-1.5"
        title="Export current list to CSV"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export
      </button>
    </div>
  );

  return (
    <AppShell variant="client" pageStrip={pageStrip}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Stats Row */}
          {stats && !loading && (
            <div className="bg-white border-b px-4 py-2 flex-shrink-0">
              <div className="flex gap-6">
                {[
                  { label: 'Active', value: stats.active_searches || 0, color: 'text-blue-600' },
                  { label: 'Candidates', value: stats.total_candidates || 0, color: 'text-gray-900' },
                  { label: 'In Review', value: stats.in_review || 0, color: 'text-amber-600' },
                  { label: 'Interviewing', value: stats.interviewing || stats.in_interview || 0, color: 'text-purple-600' },
                ].map(s => (
                  <div key={s.label} className="flex items-baseline gap-1.5">
                    <span className={`text-lg font-bold ${s.color}`}>{s.value}</span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="bg-white border-b px-4 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">
                {activeTab === 'active' ? 'Active Searches' : activeTab === 'completed' ? 'Completed' : 'Drafts'}
              </span>

              <div className="w-px h-5 bg-gray-200" />

              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-44 pl-7 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
              >
                <option value="created_at">Newest First</option>
                <option value="name">Name A-Z</option>
                <option value="candidates">Most Candidates</option>
              </select>

              <div className="flex-1" />

              {/* New Search */}
              <button
                onClick={() => setShowRequestForm(true)}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Search
              </button>

              {/* Count */}
              <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                <span className="font-medium text-gray-500">{filtered.length}</span>
                {' '}{activeTab === 'draft' ? 'drafts' : 'searches'}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-gray-500 text-sm">
                  {activeTab === 'draft' ? 'No draft searches' : activeTab === 'completed' ? 'No completed searches' : 'No active searches'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Create a new search to get started</p>
                <button
                  onClick={() => setShowRequestForm(true)}
                  className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  + New Search
                </button>
              </div>
            ) : (
              <table className="w-full text-sm" style={{ minWidth: 'max-content' }}>
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Search Name</th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Candidates</th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Stages</th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="w-28"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(pipeline => {
                    const chips = getStageChips(pipeline);
                    const total = getTotalCandidates(pipeline);
                    return (
                      <tr
                        key={pipeline.id}
                        onClick={() => {
                          if (pipeline.status === 'draft') return;
                          navigate(`/client/pipeline/${pipeline.id}`);
                        }}
                        className={`hover:bg-blue-50/50 transition-colors group ${pipeline.status !== 'draft' ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-2.5">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{pipeline.name}</div>
                            {pipeline.submitted_at && (
                              <div className="text-[10px] text-gray-400">
                                Submitted {new Date(pipeline.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-600">{pipeline.location || '—'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            total > 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
                          }`}>
                            {total}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {chips.length > 0 ? chips.map((chip, idx) => (
                              <span
                                key={idx}
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${getStageColor(chip.name)}`}
                              >
                                {chip.name}: {chip.count}
                              </span>
                            )) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">
                            {new Date(pipeline.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </td>
                        <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {pipeline.status === 'draft' ? (
                              <>
                                <button
                                  onClick={() => handleSubmitPipeline(pipeline.id)}
                                  className="px-2 py-0.5 text-[10px] bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                                >
                                  Submit
                                </button>
                                <button
                                  onClick={(e) => handleDeletePipeline(pipeline.id, pipeline.name, e)}
                                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                  title="Delete draft"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setSelectedPipelineId(pipeline.id); setManagingStages(true); }}
                                  className="px-2 py-0.5 text-[10px] text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                                >
                                  Stages
                                </button>
                                <button
                                  onClick={(e) => handleToggleCompletion(pipeline.id, e)}
                                  className="px-2 py-0.5 text-[10px] text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                                >
                                  {pipeline.is_completed ? 'Reopen' : 'Complete'}
                                </button>
                                <button
                                  onClick={(e) => handleDeletePipeline(pipeline.id, pipeline.name, e)}
                                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {managingStages && selectedPipelineId && (
        <ClientStageManager
          pipelineId={selectedPipelineId}
          onClose={() => { setManagingStages(false); setSelectedPipelineId(null); }}
          onUpdate={loadDashboard}
        />
      )}

      {showRequestForm && (
        <PipelineRequestForm
          onClose={() => { setShowRequestForm(false); setEditingPipeline(null); }}
          onSuccess={loadDashboard}
          existingPipeline={editingPipeline}
        />
      )}

      {submitModal && (
        <SubmitSearchModal
          pipelineId={submitModal.id}
          pipelineName={submitModal.name}
          onClose={() => setSubmitModal(null)}
          onSubmitted={() => { setSubmitModal(null); loadDashboard(); }}
        />
      )}
    </AppShell>
  );
};

export default ClientDashboard;
